import { useCallback, useEffect, useState } from "react";
import {
  resolveSparksCancellationWinback,
  type SparksCancellationWinbackResolution,
  type SparksCancellationWinbackResolver,
} from "./SparksCancellationWinbackResolver";

export type SparksCancellationWinbackControllerState =
  | { status: "loading" }
  | { status: "resolved"; resolution: SparksCancellationWinbackResolution }
  | { status: "error" };

const initialState = (winbackId: string): SparksCancellationWinbackControllerState =>
  winbackId.trim() ? { status: "loading" } : { status: "resolved", resolution: "invalid" };

export const useSparksCancellationWinbackController = (
  winbackId: string,
  resolver: SparksCancellationWinbackResolver = resolveSparksCancellationWinback,
) => {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<SparksCancellationWinbackControllerState>(() => initialState(winbackId));

  useEffect(() => {
    const normalizedWinbackId = winbackId.trim();
    if (!normalizedWinbackId) {
      setState({ status: "resolved", resolution: "invalid" });
      return;
    }

    const abortController = new AbortController();
    setState({ status: "loading" });

    void resolver(normalizedWinbackId, { signal: abortController.signal })
      .then((resolution) => {
        if (!abortController.signal.aborted) {
          setState({ status: "resolved", resolution });
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setState({ status: "error" });
        }
      });

    return () => abortController.abort();
  }, [attempt, resolver, winbackId]);

  const retry = useCallback(() => setAttempt((currentAttempt) => currentAttempt + 1), []);

  return { ...state, retry };
};
